import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  getPaginationRowModel,
} from '@tanstack/react-table'
import React, {Dispatch, MouseEventHandler, ReactNode, useEffect, useState} from 'react'
import {Form, Row, Table} from 'react-bootstrap'
import {PaginationControl} from 'react-bootstrap-pagination-control'
import {IPageMeta} from '../interface/paging/meta-paging'
import {DEFAULT_META, DEFAULT_PAGE_OPTION} from '../constants/page-option/page-option'
import {IPageOption} from '../interface/paging/paging'

type Props = {
  children?: ReactNode
  columns: any[]
  data: any[]
  meta: IPageMeta
  setMeta?: Dispatch<any>
  pageOption: IPageOption
  setPageOption: Dispatch<any>
  disabledLimit?: boolean
  doing?: boolean
  isSort?: boolean
}
type SortOrder = 'asc' | 'desc' | false

const TableData: React.FC<Props> = ({
  data,
  columns,
  meta = DEFAULT_META,
  setMeta,
  pageOption = DEFAULT_PAGE_OPTION,
  setPageOption,
  disabledLimit,
  doing,
  children,
  isSort,
}) => {
  const [sortData, setSortData] = useState<any[]>(JSON.parse(JSON.stringify(data)))
  let table = useReactTable({
    data: isSort ? sortData : data,
    columns,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 1000,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })
  useEffect(() => {
    if (data.length === 0) return
    setSortData(data)
  }, [data])

  function sortArray<T extends Record<string, any>>(
    array: T[],
    key: string,
    order: SortOrder = 'asc'
  ): T[] {
    if (order === false) return array

    return [...array].sort((a, b) => {
      const valA = a[key]
      const valB = b[key]

      if (typeof valA === 'string' && typeof valB === 'string') {
        return order === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA)
      }

      if (typeof valA === 'number' && typeof valB === 'number') {
        return order === 'asc' ? valA - valB : valB - valA
      }

      return 0
    })
  }

  const handleSortingOption = (header: any) => {
    if (header.column.id === 'action') return

    if (isSort) {
      const sortedByName = sortArray(data, header.column.id, header?.column?.getNextSortingOrder())
      setSortData(sortedByName)
      return
    }

    if (header?.column?.getNextSortingOrder()) {
      setPageOption({
        ...pageOption,
        sortBy: header.column.id,
        order: header?.column?.getNextSortingOrder()
          ? header?.column?.getNextSortingOrder()?.toUpperCase()
          : 'DESC',
      })
    } else {
      setPageOption({
        ...pageOption,
        sortBy: null,
        order: 'DESC',
      })
    }
  }
  return (
    <>
      {doing ? (
        <>
          <p className='placeholder-glow mt-2'>
            <span className='placeholder bg-secondary col-12' style={{height: 100}}></span>
          </p>
        </>
      ) : (
        <>
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{children}</span>
            </div>
          </div>
          <div
            style={{
              overflow: 'auto', //our scrollable table container
              position: 'relative', //needed for sticky header
            }}
          >
            <Table>
              <thead>
                {table?.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={() => handleSortingOption(header)}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              className={
                                header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                              }
                              onClick={header.column.getToggleSortingHandler()}
                              title={
                                header.column.getCanSort()
                                  ? header.column.getNextSortingOrder() === 'asc'
                                    ? 'Sort ascending'
                                    : header.column.getNextSortingOrder() === 'desc'
                                    ? 'Sort descending'
                                    : 'Clear sort'
                                  : undefined
                              }
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽',
                              }[header.column.getIsSorted() as string] ?? null}
                              {header.id !== 'isActive' &&
                                header.column.getNextSortingOrder() === 'asc' && (
                                  <span style={{width: 19, height: 19, display: 'inline-flex'}} />
                                )}
                              {header.id == 'isActive' &&
                                header.column.getNextSortingOrder() == 'desc' && (
                                  <span style={{width: 19, height: 19, display: 'inline-flex'}} />
                                )}
                            </div>
                          )}
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>
              {data?.length > 0 ? (
                <>
                  <tbody>
                    {table.getRowModel().rows.map((row) => {
                      return (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => {
                            return (
                              <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                </>
              ) : (
                <>
                  <tbody>
                    <tr>
                      <td colSpan={columns.length}>
                        <div className='center'>No Data</div>
                      </td>
                    </tr>
                  </tbody>
                </>
              )}
            </Table>
          </div>
          <div className={'justify-end'}>
            <PaginationControl
              page={meta.page}
              between={3}
              total={meta.total}
              limit={meta.limit}
              ellipsis={1}
              changePage={(page) => {
                setPageOption({...pageOption, page})
              }}
            />
            {!disabledLimit && pageOption.limit > 0 && (
              <div>
                <Form.Select
                  value={pageOption.limit}
                  onChange={(e) => {
                    setPageOption({...pageOption, page: 1, limit: parseInt(e.target.value)})
                  }}
                >
                  {/* <option value={1}>1</option>
                  <option value={2}>2</option> */}
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}
export default TableData
